import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Combobox,
  InlineStack,
  OptionCard,
  OptionCardGroup,
  Text,
  TextField,
  TextLabel,
  Toggle,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { searchManualSegmentsQueryOptions, useSegmentOptional } from '@/services/db/segment/queries';
import { isEmptyString } from '@/utils/string';
import type { SegmentChoice, SettingsFormValues } from '../settings';

const SEARCH_DEBOUNCE_MS = 300;
const NO_SEGMENTS_TOOLTIP = "You don't have any manual segments in this workspace.";

/**
 * The workspace's manual segments matching what was typed, from the server. An empty term lists
 * the first page, which also tells whether the workspace has any manual segment at all.
 */
function useManualSegmentOptions(workspaceId: number) {
  const [typed, setTyped] = useState<string | null>(null);
  const [debounced] = useDebouncedValue(typed ?? '', SEARCH_DEBOUNCE_MS);
  const term = isEmptyString(typed) ? '' : debounced;
  const query = useQuery(searchManualSegmentsQueryOptions(workspaceId, term));
  const options = (query.data ?? []).map((segment) => ({ value: segment.id, label: segment.name }));
  return {
    typed,
    setTyped,
    options,
    isLoading: query.isFetching || term !== (typed ?? ''),
    hasNone: term === '' && query.isSuccess && options.length === 0,
  };
}

type SegmentChoiceCardsProps = {
  choice: SegmentChoice;
  /** The workspace has no manual segment, so there is nothing existing to pick. */
  hasNone: boolean;
  isReadOnly: boolean;
  onChange: (patch: Partial<SettingsFormValues>) => void;
};

function SegmentChoiceCards({ choice, hasNone, isReadOnly, onChange }: SegmentChoiceCardsProps) {
  return (
    <OptionCardGroup
      ariaLabel="Segment"
      inputType="radio"
      value={choice}
      isDisabled={isReadOnly}
      onChange={(value: string) => onChange({ segmentChoice: value === 'existing' ? 'existing' : 'new' })}
      cardsDirection="row"
      cardsGap="200"
    >
      <OptionCard
        value="new"
        title="Create a new segment"
        ariaLabel="Create a new segment"
        data-test-create-sql-import-segment-type="new"
      />
      <Tooltip isDisabled={!hasNone} content={NO_SEGMENTS_TOOLTIP} isCustom={true}>
        {(context) => (
          <OptionCard
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            value="existing"
            title="Sync to an existing segment"
            ariaLabel="Sync to an existing segment"
            canSelect={!hasNone}
            data-test-create-sql-import-segment-type="existing"
          />
        )}
      </Tooltip>
    </OptionCardGroup>
  );
}

type SegmentPickerProps = {
  values: Pick<SettingsFormValues, 'segmentChoice' | 'segmentName' | 'segmentId'>;
  errors: { segmentName?: string; segmentId?: string };
  segments: ReturnType<typeof useManualSegmentOptions>;
  /** The name of the segment the draft already points at, shown until something is typed. */
  selectedName: string;
  isReadOnly: boolean;
  onChange: (patch: Partial<SettingsFormValues>) => void;
};

/** The name of the segment to create, or the manual segment to reuse. */
function SegmentPicker({ values, errors, segments, selectedName, isReadOnly, onChange }: SegmentPickerProps) {
  if (values.segmentChoice === 'new') {
    return (
      <TextField
        ariaLabel="Segment name"
        placeholder="Segment name"
        autoFocus
        value={values.segmentName}
        error={errors.segmentName}
        isDisabled={isReadOnly}
        data-test-create-sql-import-input="segment-name"
        onInput={(event) => {
          if (event.target instanceof HTMLInputElement) {
            onChange({ segmentName: event.target.value });
          }
        }}
      />
    );
  }
  return (
    <Combobox
      ariaLabel="Select segment"
      placeholder="Select segment"
      inputValue={segments.typed ?? selectedName}
      onValueChange={segments.setTyped}
      // Typing is a search, not a choice: leaving the field shows the committed segment again.
      onBlur={() => segments.setTyped(null)}
      selectedValue={isEmptyString(values.segmentId) ? null : values.segmentId}
      onSelectionChange={(value) => {
        segments.setTyped(null);
        onChange({ segmentId: value ?? '' });
      }}
      options={segments.options}
      isLoading={segments.isLoading}
      shouldFilter={false}
      error={errors.segmentId}
      isDisabled={isReadOnly}
      data-test-create-sql-import-select="segment"
    />
  );
}

/** Turning the segment off also forgets which one was chosen. */
function segmentToggle(checked: boolean): Partial<SettingsFormValues> {
  return checked ? { syncToSegment: true } : { syncToSegment: false, segmentId: '', segmentName: '' };
}

type SegmentFieldsProps = {
  workspaceId: number;
  values: Pick<SettingsFormValues, 'syncToSegment' | 'segmentChoice' | 'segmentName' | 'segmentId'>;
  errors: { segmentName?: string; segmentId?: string };
  canCreateSegment: boolean;
  isReadOnly: boolean;
  onChange: (patch: Partial<SettingsFormValues>) => void;
};

/**
 * Whether a people import also fills a segment, and which: a new one by name, or one of the
 * workspace's manual segments. Without the permission to create segments only the latter offers.
 */
export function SegmentFields({
  workspaceId,
  values,
  errors,
  canCreateSegment,
  isReadOnly,
  onChange,
}: SegmentFieldsProps) {
  const taxonomy = useProductTaxonomy();
  const segments = useManualSegmentOptions(workspaceId);
  const selected = useSegmentOptional({
    workspaceId,
    id: values.segmentId,
    enabled: values.segmentChoice === 'existing' && !isEmptyString(values.segmentId),
  });
  const selectedName = selected.data?.name ?? '';

  return (
    <Box>
      <InlineStack alignBlock="center">
        <TextLabel id="sync-to-segment-label" htmlFor="sync-to-segment" flexGrow="1">
          Sync these {taxonomy.term('people.plural')} to a segment?{' '}
          <Text color="subtle" weight="regular">
            (optional)
          </Text>
        </TextLabel>
        <Toggle
          id="sync-to-segment"
          ariaLabelledby="sync-to-segment-label"
          size="sm"
          isChecked={values.syncToSegment}
          isDisabled={isReadOnly}
          data-test-create-sql-import-switch="sync-to-segment"
          onChange={(event) => onChange(segmentToggle(event.target.checked))}
        />
      </InlineStack>
      {values.syncToSegment ? (
        <Box mt="150">
          {canCreateSegment ? (
            <SegmentChoiceCards
              choice={values.segmentChoice}
              hasNone={segments.hasNone}
              isReadOnly={isReadOnly}
              onChange={onChange}
            />
          ) : null}
          <Box mt="150">
            <SegmentPicker
              values={values}
              errors={errors}
              segments={segments}
              selectedName={selectedName}
              isReadOnly={isReadOnly}
              onChange={onChange}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export type { SegmentChoice };
